import { ArrowLeftIcon, PlusIcon } from "@heroicons/react/24/outline";
import React, { useCallback, useState } from "react";
import { toast, Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import {
  AnalysisBuilderMode,
  FreeFormMode,
  generatePrompt,
  type InterfaceMode,
  NewChatModal,
  type PlayerDataSet,
  type ReportSection,
  type ReportSettings,
} from "..";
import RL_Logo from "../../assets/RL_Logo.svg";
import { SuccessToast } from "../../components";
import { useAppSelector } from "../../redux/hooks";
import { selectOrg } from "../../redux/slices/orgSlice";
import { selectPlayers } from "../../redux/slices/playerSlice";
import {
  AiChatRequest,
  ChatMessage as ApiChatMessage,
  ChatMessageContent,
} from "../../services/aiChat";
import {
  ExportChatModal,
  ExportDropdown,
  ImportChatModal,
} from "./components/shared";
import { useStreamChat } from "./hooks";

// Instruct the AI on allowed output formats (sent in custom_context and appended to each user message so the model always sees it)
export const REBOOT_LIVE_REPORT_CONTEXT = [
  "You cannot provide downloadable files (e.g. PDF, Word, or any file attachment).",
  "Your response must always be plain text, markdown, or HTML only.",
  "Never claim you created, saved, copied, or wrote a file;",
  "never mention OUTPUT_DIR, download links, or 'the file is ready for download.'",
  "If the user asks for an HTML report (or does not specify a format),",
  "you MUST output the complete HTML in your response—inside a ```html ... ``` code block—so they can view it in the app.",
  "If they ask for an unsupported format (e.g. PDF),",
  "respond briefly in plain text that the format is not currently supported",
  "and offer HTML, markdown, or plain text instead—do not pretend you generated a file.",
  "Output content directly in your message only.",
  "When your text response lists findings, metrics, or multiple points,",
  "format them as a markdown bullet list (using '- ') or a numbered list,",
  "not as plain lines, so they render as a properly spaced list.",
  "If a report_template.py file is available in the container,",
  "prefer using `from report_template import build_report` to create the report structure.",
  "Pass your analysis sections and Chart.js code to build_report() instead of writing HTML from scratch.",
  "If any SKILL.md files are available, read them for usage instructions and available CSS classes.",
].join(" ");

export const REBOOT_LIVE_REPORT_INSTRUCTION = [
  "Important: You cannot create, save, or provide files.",
  "Return only plain text, markdown, or HTML.",
  "Never say you created a PDF/report file, copied it somewhere, or that it is ready for download.",
  "If generating an HTML report,",
  "you MUST output the full HTML in a ```html ... ``` code block in this same message.",
  "Do not say 'here is the HTML report' or 'copy between the backticks'",
  "unless you have actually output the complete HTML block in this response.",
  "Do not mention OUTPUT_DIR or any file path.",
].join(" ");

const DEFAULT_STREAM_ERROR_SUGGESTION =
  "You can try sending your message again or ask a shorter follow-up.";

/** Max number of messages from stored history to send on follow-ups. We send this many prior messages plus the new user message. */
const MAX_HISTORY_MESSAGES_ON_FOLLOW_UP = 50;

/** Parse once; return user-facing message + suggestion */
function getStreamErrorDisplay(raw: string | undefined): {
  message: string;
  suggestion: string;
} {
  if (!raw?.trim()) {
    return {
      message: "Something went wrong. Please try again.",
      suggestion: DEFAULT_STREAM_ERROR_SUGGESTION,
    };
  }
  const s = raw.trim();
  let inner = "";
  const quotedMessage = s.match(
    /'message':\s*'([^']+)'|"message":\s*"([^"]+)"/,
  );
  if (quotedMessage) {
    inner = (quotedMessage[1] ?? quotedMessage[2] ?? "").trim();
  }
  if (!inner) {
    try {
      const parsed = JSON.parse(s);
      inner = parsed?.error?.message ?? parsed?.message ?? "";
    } catch {
      if (s.length > 120) {
        if (s.toLowerCase().includes("unable to communicate"))
          return {
            message: "We couldn't reach the AI service. Please try again.",
            suggestion: DEFAULT_STREAM_ERROR_SUGGESTION,
          };
        const first = s.split(/[.\n]/)[0]?.trim();
        return {
          message:
            first && first.length < 100
              ? first
              : "The request failed. Please try again.",
          suggestion: DEFAULT_STREAM_ERROR_SUGGESTION,
        };
      }
      return { message: s, suggestion: DEFAULT_STREAM_ERROR_SUGGESTION };
    }
  }
  const lower = inner.toLowerCase();
  // Input limit: prompt + uploaded data exceeds backend token maximum (e.g. 200000)
  if (
    lower.includes("prompt is too long") ||
    (lower.includes("tokens") &&
      lower.includes("maximum") &&
      !lower.includes("output tokens"))
  ) {
    return {
      message:
        "The uploaded data plus your prompt exceeds the maximum input size.",
      suggestion:
        "Try fewer sessions, a shorter date range, or a shorter prompt.",
    };
  }
  if (
    lower.includes("max_tokens") ||
    (lower.includes("maximum") && lower.includes("output tokens"))
  ) {
    return {
      message:
        "The response would be too long for the AI to generate. Try a shorter question or ask for a smaller report.",
      suggestion: DEFAULT_STREAM_ERROR_SUGGESTION,
    };
  }
  if (
    lower.includes("timeout") &&
    (lower.includes("stream") ||
      lower.includes("interrupted") ||
      /\d+\s*s\s*timeout/.test(lower))
  ) {
    return {
      message: "The response was cut off due to a time limit.",
      suggestion: "Try a shorter request, fewer sessions, or a simpler report.",
    };
  }
  if (lower.includes("timeout") || lower.includes("interrupted")) {
    return {
      message:
        "The request took too long or was interrupted. Please try again.",
      suggestion: DEFAULT_STREAM_ERROR_SUGGESTION,
    };
  }
  if (
    lower.includes("stream connection failed") ||
    lower.includes("connection failed") ||
    lower.includes("err_http2")
  ) {
    return {
      message: "The connection was interrupted.",
      suggestion: "Check your connection and try again.",
    };
  }
  if (lower.includes("unable to communicate")) {
    return {
      message: "We couldn't reach the AI service. Please try again.",
      suggestion: DEFAULT_STREAM_ERROR_SUGGESTION,
    };
  }
  return { message: inner, suggestion: DEFAULT_STREAM_ERROR_SUGGESTION };
}

interface Dataset {
  players: string[];
  movementTypeId: string;
  files: File[];
  sessions: string[];
  selectedSessionDate?: string;
  startDate?: Date;
  endDate?: Date;
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  /** Set when the stream failed (connection/timeout); surfaces a prominent error banner */
  streamError?: { message: string; suggestion: string };
}

interface ChatSessionData {
  sessionId: string;
  containerId: string;
  apiMessages: ApiChatMessage[];
}

/** Strip REBOOT_LIVE_REPORT_INSTRUCTION from user message text */
function stripReportInstruction(text: string): string {
  const instructionIdx = text.indexOf("\n\nImportant: You cannot create");
  return instructionIdx !== -1 ? text.slice(0, instructionIdx).trim() : text;
}

export const RebootLiveInterface: React.FC = () => {
  const navigate = useNavigate();

  // Redux state
  const players = useAppSelector(selectPlayers);
  const org = useAppSelector(selectOrg);

  // Interface mode - "freeform" or "analysisbuilder". Free Form is hidden for
  // now (no toggle exposed, forced to Analysis Builder) but FreeFormMode is
  // kept intact so the toggle can be restored later.
  const [interfaceMode] = useState<InterfaceMode>("analysisbuilder");

  // View state - controls whether we show setup or conversation
  const [isChatActive, setIsChatActive] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);

  // Local state
  const [inputValue, setInputValue] = useState("");
  const [dataset, setDataset] = useState<Dataset>({
    players: [],
    movementTypeId: "",
    files: [],
    sessions: [],
  });
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Analysis Builder state
  // Bumped on Reset to remount DefinePlayerDataSet, clearing its internal
  // load/checkbox state (see AnalysisBuilderMode's resetKey prop)
  const [analysisBuilderResetKey, setAnalysisBuilderResetKey] = useState(0);
  const [analysisBuilderPlayerData, setAnalysisBuilderPlayerData] =
    useState<PlayerDataSet>({
      playerId: "",
      movementTypeId: "",
      sessionDates: [],
      sessionIds: [],
      isLoaded: false,
      hasStartedLoading: false,
      isUploading: false,
      containerId: "",
      fileIds: [],
      filesList: [],
      dataSummary: "",
    });

  const [reportSettings, setReportSettings] = useState<ReportSettings>({
    analysisTone: "",
    targetAudience: "",
    outputFormat: "html-report",
    includeRecommendations: "",
    additionalContext: "",
  });

  // Analysis sections state
  const [reportSections, setReportSections] = useState<ReportSection[]>([
    {
      id: Date.now().toString(),
      title: "",
      config: {
        metricsFocus: "",
        analysisStructure: "",
        visualization: "",
      },
      description: "",
    },
  ]);

  // Reset Analysis Builder to initial state
  const handleAnalysisBuilderReset = useCallback(() => {
    setAnalysisBuilderPlayerData({
      playerId: "",
      movementTypeId: "",
      sessionDates: [],
      sessionIds: [],
      isLoaded: false,
      hasStartedLoading: false,
      isUploading: false,
      containerId: "",
      fileIds: [],
      filesList: [],
      dataSummary: "",
    });
    setReportSettings({
      analysisTone: "",
      targetAudience: "",
      outputFormat: "html-report",
      includeRecommendations: "",
      additionalContext: "",
    });
    setReportSections([
      {
        id: Date.now().toString(),
        title: "",
        config: {
          metricsFocus: "",
          analysisStructure: "",
          visualization: "",
        },
        description: "",
      },
    ]);
    setAnalysisBuilderResetKey((key) => key + 1);
  }, []);

  // Selected report HTML for download functionality
  const [selectedReportHtml, setSelectedReportHtml] = useState<string | null>(
    null,
  );

  // Chat session data (from ChatSetup when initiating chat)
  const [chatSessionData, setChatSessionData] =
    useState<ChatSessionData | null>(null);

  // Streaming chat hook
  const { streamChat, isStreaming, abort } = useStreamChat({
    onStart: () => {
      // Add a placeholder assistant message that will be updated with streamed content
      const placeholderMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, placeholderMessage]);
    },
    onContent: (_chunk, _fullContent, textContent) => {
      // Display the text-only stream, which skips tool_use/non-text blocks the
      // backend sends. The backend always tags blocks, so a turn made entirely of
      // non-text blocks renders nothing rather than leaking the raw tool JSON.
      setMessages((prev) => {
        const updated = [...prev];
        const lastMessage = updated[updated.length - 1];
        if (lastMessage && lastMessage.role === "assistant") {
          return [
            ...updated.slice(0, -1),
            { ...lastMessage, content: textContent },
          ];
        }
        return updated;
      });
    },
    onComplete: (_content, _stopReason, _usage, containerId, textContent) => {
      const displayContent = textContent ?? "";
      // Final update with complete content
      setMessages((prev) => {
        const updated = [...prev];
        const lastMessage = updated[updated.length - 1];
        if (lastMessage && lastMessage.role === "assistant") {
          return [
            ...updated.slice(0, -1),
            { ...lastMessage, content: displayContent },
          ];
        }
        return updated;
      });
      // Append assistant message to apiMessages and update containerId if returned
      setChatSessionData((prev) =>
        prev
          ? {
              ...prev,
              containerId: containerId || prev.containerId,
              apiMessages: [
                ...prev.apiMessages,
                {
                  role: "assistant" as const,
                  content: [{ type: "text" as const, text: displayContent }],
                },
              ],
            }
          : null,
      );
    },
    onError: (error) => {
      console.error("Stream error:", error);
      const { message: errorText, suggestion } = getStreamErrorDisplay(
        error?.message,
      );
      const streamError = { message: errorText, suggestion };
      const errorContentRef = { current: "" };
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        if (last.role === "assistant") {
          // Keep incomplete content; surface error via streamError banner (no inline append)
          errorContentRef.current = last.content.trim()
            ? last.content +
              `\n\n— The response was cut off due to an error. ${errorText}. ${suggestion}`
            : `Sorry, something went wrong: ${errorText} ${suggestion}`;
          return [
            ...prev.slice(0, -1),
            { ...last, content: last.content, streamError },
          ];
        }
        // No assistant message yet (e.g. error before onStart); add one so the error is visible
        errorContentRef.current = `Sorry, something went wrong: ${errorText} ${suggestion}`;
        return [
          ...prev,
          {
            id: `assistant-error-${Date.now()}`,
            role: "assistant",
            content: errorContentRef.current,
            timestamp: new Date(),
            streamError,
          },
        ];
      });
      // Add error message to apiMessages so export includes full conversation (fixes import order)
      // Read ref inside updater so it's set after React runs the setMessages callback
      setChatSessionData((prev) => {
        const contentToAdd = errorContentRef.current;
        return prev && contentToAdd
          ? {
              ...prev,
              apiMessages: [
                ...prev.apiMessages,
                {
                  role: "assistant" as const,
                  content: [{ type: "text" as const, text: contentToAdd }],
                },
              ],
            }
          : prev;
      });
    },
  });

  // Build the API message with text and optional file references for container upload
  const buildChatMessageContent = useCallback(
    (textForApi: string, fileIds?: string[]): ChatMessageContent[] => {
      const content: ChatMessageContent[] = [
        { type: "text", text: textForApi },
      ];
      if (fileIds) {
        for (const fileId of fileIds) {
          content.push({
            type: "container_upload",
            file_id: fileId,
          });
        }
      }
      return content;
    },
    [],
  );

  // Generate report prompt, send to /ai/chat, and open chat conversation
  const handleAnalysisBuilderGenerate = useCallback(() => {
    const player = players.playerList.find(
      (p) => p.orgPlayerId === analysisBuilderPlayerData.playerId,
    );
    const playerName = player
      ? `${player.firstName} ${player.lastName}`
      : "[PLAYER_NAME]";

    const movement = org.details.movementTypes.find(
      (m) => m.id.toString() === analysisBuilderPlayerData.movementTypeId,
    );
    const movementTypeName = movement ? movement.name : "[MOVEMENT_TYPE]";

    const basePrompt = generatePrompt({
      playerData: analysisBuilderPlayerData,
      settings: reportSettings,
      sections: reportSections,
      playerName,
      movementTypeName,
    });
    // Inline Claude's data summary (from the silent warm-up) so it travels in
    // the user message — matches what users see in the Report Prompt textarea.
    const dataSummary = analysisBuilderPlayerData.dataSummary;
    const prompt = dataSummary
      ? `${basePrompt}\n\n## DATA SUMMARY\n\n${dataSummary}`
      : basePrompt;

    const sessionId = `session_${crypto.randomUUID()}`;
    const containerId = analysisBuilderPlayerData.containerId;

    const textForApi = `${prompt}\n\n${REBOOT_LIVE_REPORT_INSTRUCTION}`;
    const messageContent = buildChatMessageContent(textForApi);
    const userApiMessage: ApiChatMessage = {
      role: "user",
      content: messageContent,
    };

    setChatSessionData({
      sessionId,
      containerId,
      apiMessages: [userApiMessage],
    });
    setMessages([
      {
        id: `user-${Date.now()}`,
        role: "user",
        content: prompt,
        timestamp: new Date(),
      },
    ]);
    streamChat({
      session_id: sessionId,
      messages: [userApiMessage],
      container_id: containerId,
      custom_context: REBOOT_LIVE_REPORT_CONTEXT,
    });
    setDataset({
      players: analysisBuilderPlayerData.playerId
        ? [analysisBuilderPlayerData.playerId]
        : [],
      movementTypeId: analysisBuilderPlayerData.movementTypeId || "",
      files: [],
      sessions: analysisBuilderPlayerData.sessionIds || [],
      selectedSessionDate: analysisBuilderPlayerData.sessionDates?.[0]
        ? analysisBuilderPlayerData.sessionDates[0].toISOString().slice(0, 10)
        : undefined,
    });
    setIsChatActive(true);
    setInputValue("");
  }, [
    buildChatMessageContent,
    analysisBuilderPlayerData,
    reportSettings,
    reportSections,
    players,
    org,
    streamChat,
  ]);

  // Handle initiating chat from ChatSetup (initial message with data)
  const handleInitiateChatWithData = useCallback(
    async (
      sessionId: string,
      containerId: string,
      userMessageText: string,
      fileIds?: string[],
      dataSummary?: string,
    ) => {
      const textForApi = `${userMessageText}\n\n${REBOOT_LIVE_REPORT_INSTRUCTION}`;
      const messageContent = buildChatMessageContent(textForApi, fileIds);
      const userApiMessage: ApiChatMessage = {
        role: "user",
        content: messageContent,
      };

      // Store the chat session data for subsequent messages
      setChatSessionData({
        sessionId,
        containerId,
        apiMessages: [userApiMessage],
      });
      setMessages([
        {
          id: `user-${Date.now()}`,
          role: "user",
          content: userMessageText,
          timestamp: new Date(),
        },
      ]);

      // Include data summary in custom_context so the model can skip data exploration
      const customContext = dataSummary
        ? `${REBOOT_LIVE_REPORT_CONTEXT}\n\n${dataSummary}`
        : REBOOT_LIVE_REPORT_CONTEXT;

      streamChat({
        session_id: sessionId,
        messages: [userApiMessage],
        container_id: containerId,
        custom_context: customContext,
      });
      setIsChatActive(true);
      setInputValue("");
    },
    [buildChatMessageContent, streamChat],
  );

  // Handle sending subsequent messages in the conversation
  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || !chatSessionData) return;

    // Add user message to display
    const userDisplayMessage: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      content: inputValue,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userDisplayMessage]);

    // Build the new user API message; the backend reuses the existing container_id
    const textForApi = `${inputValue.trim()}\n\n${REBOOT_LIVE_REPORT_INSTRUCTION}`;
    const followUpContent = buildChatMessageContent(textForApi);
    const userApiMessage: ApiChatMessage = {
      role: "user",
      content: followUpContent,
    };

    // apiMessages includes the previous assistant message (added in onComplete when stream finished)
    // Add the new user message
    const updatedApiMessages = [...chatSessionData.apiMessages, userApiMessage];

    // Update stored API messages
    setChatSessionData((prev) =>
      prev ? { ...prev, apiMessages: updatedApiMessages } : null,
    );

    // Send conversation history for context (capped to avoid token limits)
    const recentHistory = chatSessionData.apiMessages.slice(
      -MAX_HISTORY_MESSAGES_ON_FOLLOW_UP,
    );
    const messagesForRequest: ApiChatMessage[] = [
      ...recentHistory,
      userApiMessage,
    ];

    const chatRequest: AiChatRequest = {
      session_id: chatSessionData.sessionId,
      messages: messagesForRequest,
      container_id: chatSessionData.containerId,
      custom_context: REBOOT_LIVE_REPORT_CONTEXT,
    };

    // Clear input and start streaming
    setInputValue("");
    streamChat(chatRequest);
  }, [inputValue, buildChatMessageContent, chatSessionData, streamChat]);

  const handleExportChat = useCallback(async (): Promise<
    { filename: string; sizeBytes: number } | undefined
  > => {
    if (!chatSessionData || chatSessionData.apiMessages.length === 0) return;

    // Include the final assistant message from display if not yet in apiMessages
    // (apiMessages only gets it when user sends the next message)
    let exportApiMessages = [...chatSessionData.apiMessages];
    const lastMessage = messages[messages.length - 1];
    const lastInApi = exportApiMessages[exportApiMessages.length - 1];
    if (
      lastMessage?.role === "assistant" &&
      lastMessage.content &&
      lastInApi?.role === "user" &&
      !isStreaming
    ) {
      exportApiMessages.push({
        role: "assistant",
        content: [{ type: "text", text: lastMessage.content }],
      });
    }

    // Strip REBOOT_LIVE_REPORT_INSTRUCTION from user messages in export
    exportApiMessages = exportApiMessages.map((msg) => {
      if (msg.role !== "user") return msg;
      const textContent = msg.content.find(
        (c): c is { type: "text"; text: string } =>
          c &&
          typeof c === "object" &&
          (c as { type?: string }).type === "text",
      );
      if (!textContent) return msg;
      const stripped = stripReportInstruction(textContent.text);
      return {
        ...msg,
        content: [{ type: "text" as const, text: stripped }],
      };
    });

    const resolvedPlayerNames = dataset.players
      .map((orgPlayerId) => {
        const player = players.playerList.find(
          (p) => p.orgPlayerId === orgPlayerId,
        );
        return player ? `${player.firstName} ${player.lastName}` : "";
      })
      .filter(Boolean)
      .join(", ");
    const playerName =
      resolvedPlayerNames.length > 0 ? resolvedPlayerNames : undefined;
    const movement = dataset.movementTypeId
      ? org.details.movementTypes.find(
          (m) => m.id.toString() === dataset.movementTypeId,
        )
      : undefined;
    const movementType = movement?.name;

    const exportPayload = {
      version: 1,
      exportedAt: new Date().toISOString(),
      sessionId: chatSessionData.sessionId,
      containerId: chatSessionData.containerId,
      messages: exportApiMessages,
      metadata: {
        players: dataset.players,
        movementTypeId: dataset.movementTypeId,
        playerName,
        movementType,
        startDate: dataset.selectedSessionDate,
      },
    };

    const playerNames = playerName?.replace(/, /g, "_") ?? "Chat";
    const timestamp = new Date().toISOString().slice(0, 10);
    const suggestedFilename = `RebootLive_Chat_${playerNames}_${timestamp}.json`;

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
      type: "application/json",
    });

    const sizeBytes = blob.size;

    type FileSystemAccessWindow = Window & {
      showSaveFilePicker: (opts: {
        suggestedName: string;
        types: Array<{
          description: string;
          accept: Record<string, string[]>;
        }>;
      }) => Promise<{
        name: string;
        createWritable: () => Promise<{
          write: (data: Blob) => Promise<void>;
          close: () => Promise<void>;
        }>;
      }>;
    };

    if ("showSaveFilePicker" in window) {
      try {
        const handle = await (
          window as FileSystemAccessWindow
        ).showSaveFilePicker({
          suggestedName: suggestedFilename,
          types: [
            {
              description: "JSON File",
              accept: { "application/json": [".json"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return { filename: handle.name, sizeBytes };
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        console.log("File System Access API failed, using fallback", err);
      }
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedFilename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
    return { filename: suggestedFilename, sizeBytes };
  }, [chatSessionData, dataset, messages, isStreaming, players, org]);

  /** Normalize message content to string (handles both API format and prototype string format) */
  const getTextFromMessageContent = useCallback((content: unknown): string => {
    if (!content) return "";
    if (typeof content === "string") return content.trim();
    if (!Array.isArray(content)) return "";
    const textContent = content.find(
      (c): c is { type: "text"; text: string } =>
        !!c &&
        typeof c === "object" &&
        "type" in c &&
        (c as { type: unknown }).type === "text",
    );
    return textContent?.text?.trim() ?? "";
  }, []);

  const handleImportFromModal = useCallback(
    (data: {
      messages: Array<{ role: string; content: unknown }>;
      reportContent?: string;
      playerName: string;
      movementType: string;
      selectedDate: string;
      sessionId?: string;
      containerId?: string;
      metadata?: Record<string, unknown>;
    }) => {
      const { messages: rawMessages, metadata } = data;
      if (!rawMessages || rawMessages.length === 0) return;

      const displayMessages: Message[] = rawMessages.map((m, i) => ({
        id: `imported-${i}-${Date.now()}`,
        role: m.role as "user" | "assistant",
        content: getTextFromMessageContent(m.content),
        timestamp: new Date(),
      }));

      const apiMessages: ApiChatMessage[] = rawMessages.map((m) => {
        const text = getTextFromMessageContent(m.content);
        return {
          role: m.role as "user" | "assistant",
          content: [{ type: "text" as const, text }],
        };
      });

      setChatSessionData({
        sessionId: data.sessionId ?? `imported-${Date.now()}`,
        containerId: data.containerId ?? "",
        apiMessages,
      });
      setMessages(displayMessages);
      setDataset({
        players: (metadata?.players as string[]) ?? [],
        movementTypeId:
          (metadata?.movementTypeId as string) ?? data.movementType ?? "",
        files: [],
        sessions: [],
        selectedSessionDate: data.selectedDate,
      });
      setInputValue("");
      setIsChatActive(true);
      setSelectedReportHtml(
        data.reportContent?.includes("<") ? data.reportContent : null,
      );
      setIsImportModalOpen(false);
    },
    [getTextFromMessageContent],
  );

  const handleDownloadReport = async () => {
    if (!selectedReportHtml) {
      console.log("No report available to download");
      return;
    }

    // Get player names for suggested filename
    const playerNames =
      dataset.players.length > 0
        ? dataset.players
            .map((orgPlayerId) => {
              const player = players.playerList.find(
                (p) => p.orgPlayerId === orgPlayerId,
              );
              return player ? `${player.firstName} ${player.lastName}` : "";
            })
            .filter(Boolean)
            .join("_")
        : "Athlete";

    // Generate suggested filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 10);
    const suggestedFilename = `Biomechanics_Report_${playerNames}_${timestamp}.html`;

    // Create blob with HTML content
    const blob = new Blob([selectedReportHtml], { type: "text/html" });

    // Try to use File System Access API for save dialog
    if ("showSaveFilePicker" in window) {
      try {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: suggestedFilename,
          types: [
            {
              description: "HTML File",
              accept: { "text/html": [".html"] },
            },
          ],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.custom(
          <SuccessToast
            message={`Report saved as ${handle.name}`}
            classNames="mt-modal w-1/3"
          />,
          { duration: 4000, position: "top-center" },
        );
        return;
      } catch (err: any) {
        // User cancelled the dialog or API failed
        if (err.name === "AbortError") {
          return; // User cancelled, do nothing
        }
        // Fall through to standard download for other errors
        console.log("File System Access API failed, using fallback", err);
      }
    }

    // Fallback: Standard download method for browsers without File System Access API
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedFilename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();

    toast.custom(
      <SuccessToast
        message={`Report downloaded as ${suggestedFilename}`}
        classNames="mt-modal w-1/3"
      />,
      { duration: 4000, position: "top-center" },
    );

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  const handleVoiceTranscript = (transcript: string) => {
    setInputValue((prev) => prev + transcript);
  };

  const handleNewChat = () => {
    setIsNewChatModalOpen(true);
  };

  const confirmNewChat = () => {
    // Abort any ongoing stream when user confirms new chat
    abort();
    setDataset({
      players: [],
      movementTypeId: "",
      files: [],
      sessions: [],
      selectedSessionDate: undefined,
    });
    setInputValue("");
    setMessages([]);
    setIsChatActive(false);
    setIsNewChatModalOpen(false);
    setChatSessionData(null);
    setSelectedReportHtml(null);
    handleAnalysisBuilderReset();
  };

  return (
    <div className="font-['Inter'] fixed inset-0 z-[999999] bg-[#2a2a2a] flex flex-col">
      <Toaster
        containerStyle={{ zIndex: 9999999, top: 24 }}
        position="top-center"
      />
      {/* Header */}
      <div className="h-[70px] relative shrink-0 w-full bg-[#1d1d1f]">
        <div className="flex flex-row items-center size-full">
          <div className="box-border content-stretch flex h-17.5 items-center justify-between px-6 py-0 relative w-full">
            <button
              data-testid="reboot-live-back-navigation-button"
              onClick={() => {
                if (isChatActive) {
                  handleNewChat();
                } else {
                  navigate(-1);
                }
              }}
              className="content-stretch flex gap-3 items-center relative shrink-0 hover:opacity-80 transition-opacity"
            >
              <div className="flex flex-row items-center self-stretch">
                <div className="content-stretch flex gap-2.5 h-full items-center justify-center relative shrink-0">
                  <ArrowLeftIcon className="w-5 h-5 text-orange-500" />
                </div>
              </div>
              <p className="font-bold leading-9 not-italic relative shrink-0 text-base text-nowrap whitespace-pre text-orange-500">
                Back
              </p>
            </button>

            {/* Header logo - hidden when chat is active. Replaces the Free
                Form / Analysis Builder mode toggle (InterfaceModeToggle)
                while Free Form is hidden. */}
            {!isChatActive && (
              <img
                src={RL_Logo}
                alt="Reboot Live"
                data-testid="reboot-live-header-logo"
                className="h-12 object-contain"
              />
            )}

            {isChatActive ? (
              <div className="box-border content-stretch flex gap-3 items-start p-5 relative shrink-0">
                <button
                  data-testid="reboot-live-new-chat-button"
                  onClick={handleNewChat}
                  className="h-10 relative rounded-md shrink-0 hover:opacity-90 transition-opacity"
                >
                  <div className="box-border content-stretch flex gap-2 h-10 items-center justify-center overflow-clip px-2.5 py-1.75 relative rounded-md shrink-0">
                    <div className="content-stretch flex items-center justify-center relative rounded-md shrink-0">
                      <PlusIcon className="w-5 h-5 text-white" />
                    </div>
                    <p className="font-semibold leading-5 not-italic relative shrink-0 text-base text-nowrap whitespace-pre text-white">
                      New Chat
                    </p>
                  </div>
                  <div
                    aria-hidden="true"
                    className="absolute border border-solid inset-0 pointer-events-none rounded-md shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] border-orange-500"
                  />
                </button>

                <ExportDropdown
                  hasReport={!!selectedReportHtml}
                  hasChatExport={
                    !!chatSessionData && chatSessionData.apiMessages.length > 0
                  }
                  isStreaming={isStreaming}
                  onDownloadReport={handleDownloadReport}
                  onExportChat={() => setIsExportModalOpen(true)}
                />
              </div>
            ) : (
              <div className="shrink-0" />
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Chat when active (either mode), otherwise setup by mode */}
      {isChatActive ? (
        <FreeFormMode
          isChatActive={true}
          dataset={dataset}
          setDataset={setDataset}
          inputValue={inputValue}
          setInputValue={setInputValue}
          onVoiceTranscript={handleVoiceTranscript}
          onInitiateChatWithData={handleInitiateChatWithData}
          messages={messages}
          onSendMessage={handleSendMessage}
          isStreaming={isStreaming}
          onSelectedReportChange={setSelectedReportHtml}
        />
      ) : interfaceMode === "freeform" ? (
        <FreeFormMode
          isChatActive={false}
          dataset={dataset}
          setDataset={setDataset}
          inputValue={inputValue}
          setInputValue={setInputValue}
          onVoiceTranscript={handleVoiceTranscript}
          onInitiateChatWithData={handleInitiateChatWithData}
          messages={messages}
          onSendMessage={handleSendMessage}
          isStreaming={isStreaming}
          onSelectedReportChange={setSelectedReportHtml}
          onOpenImportModal={() => setIsImportModalOpen(true)}
        />
      ) : (
        <AnalysisBuilderMode
          playerData={analysisBuilderPlayerData}
          onPlayerDataChange={setAnalysisBuilderPlayerData}
          settings={reportSettings}
          onSettingsChange={setReportSettings}
          sections={reportSections}
          onSectionsChange={setReportSections}
          onReset={handleAnalysisBuilderReset}
          onGenerate={handleAnalysisBuilderGenerate}
          onOpenImportModal={() => setIsImportModalOpen(true)}
          resetKey={analysisBuilderResetKey}
        />
      )}

      {/* New Chat Modal */}
      <NewChatModal
        isOpen={isNewChatModalOpen}
        onClose={() => setIsNewChatModalOpen(false)}
        onConfirm={confirmNewChat}
      />

      {/* Import Previous Conversation Modal */}
      <ImportChatModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onResumeInChat={handleImportFromModal}
      />

      {/* Export Chat Session Modal */}
      <ExportChatModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        playerName={
          dataset.players.length > 0
            ? dataset.players
                .map((orgPlayerId) => {
                  const p = players.playerList.find(
                    (x) => x.orgPlayerId === orgPlayerId,
                  );
                  return p ? `${p.firstName} ${p.lastName}` : "";
                })
                .filter(Boolean)
                .join(", ") || "Player"
            : "Player"
        }
        movementType={
          (dataset.movementTypeId
            ? org.details.movementTypes.find(
                (m) => m.id.toString() === dataset.movementTypeId,
              )?.name
            : null) ?? "Swing"
        }
        selectedDate={dataset.selectedSessionDate ?? "N/A"}
        messageCount={messages.length}
        onExport={handleExportChat}
      />
    </div>
  );
};
